/**
 * The template quality attestation's analytics vocabulary: the decision and
 * surface unions the gate evaluates in, the payload `template_quality_gate`
 * carries, and the emit seam the app binds.
 *
 * WHAT IS HERE AND WHAT IS NOT. The vocabulary and the payload are contract —
 * `template_quality_gate` is a warehouse key and a regulatory control's audit
 * trail, so two spellings of `surface: 'template_builder'` is two populations.
 * The EMIT is not: `track()` reaches the analytics pipeline, and nothing in
 * this package emits (ruling 24). {@link TrackTemplateQualityGate} is the seam
 * — the app supplies the emit, the domain supplies the status and the context,
 * and {@link buildTemplateQualityGateEventProps} is the mapping between them,
 * which is the domain's because the property names are the contract.
 *
 * The event NAME is not here either. It is declared once, in
 * `src/contributions/analytics.ts`, through `defineAnalyticsEvent` (D9 ruling
 * 27); this file is what that descriptor's payload type points at.
 *
 * WHY THIS FILE IS NOT `contracts/analytics.ts`. The analytics drift checker
 * globs any path ending `src/contracts/analytics.ts` (arm 2) and treats what it
 * finds as a DECLARATION home. Under ruling 27 the declaration home is the
 * contributions descriptor, so a payload file with that name would claim to be
 * something it is not. `.types.ts` matches neither of the checker's patterns
 * and follows the agents/commerce precedent.
 *
 * The six vocabulary types below were EXTRACTED from the quality-gate resolver
 * atom, now `ui/template-details/templateQualityGate.atoms.ts` — history:
 * `git log --follow` on that file, which traces back through the app path they
 * were extracted from. They came with the payload rather than
 * staying behind it: `TemplateQualityGateEventProps` is not self-contained
 * without them, a `/contracts` file may not reach back into the app, and a
 * duplicated union is how one event becomes two.
 */
import { type Region } from '@heidi/schema/ml-scribe';

/** What the clinician themselves can answer at the dialog. */
export type TemplateQualityGateClinicianDecision = 'accepted' | 'declined';

/**
 * `superseded` is produced by the system, never the dialog: a second creating
 * save replaced this one while it waited. It is kept distinct from `declined`
 * because collapsing the two makes every decline-rate figure wrong.
 */
export type TemplateQualityGateDecision =
  | TemplateQualityGateClinicianDecision
  | 'superseded';

/** Entry point whose save triggered the gate. */
export type TemplateQualityGateSurface =
  | 'template_builder'
  | 'legacy_template_editor'
  | 'onboarding'
  | 'visibility'
  | 'ask_heidi_approval'
  | 'ask_heidi_approval_overlay';

/** Whether `userRegion` is trustworthy, or a fail-open assumption. */
export type TemplateQualityGateRegionStatus = 'resolved' | 'pending' | 'error';

/** The conditions one gate instance evaluates under, fixed per hook render. */
export interface TemplateQualityGateContext {
  surface: TemplateQualityGateSurface;
  userRegion: Region | undefined;
  regionStatus: TemplateQualityGateRegionStatus;
  isKillSwitchOn: boolean;
}

/** A gate context bound to the one save that raised it. */
export interface TemplateQualityGateSaveContext extends TemplateQualityGateContext {
  templateId?: string;
}

/**
 * The UK/EU quality attestation a clinician must accept before a save creates a
 * bespoke template. `shown` counts presentations and is the denominator for the
 * outcomes, with one exception: a save superseded before the lazily-loaded
 * dialog mounts resolves without ever being presented, so it reports an outcome
 * with no `shown`. `bypassed` covers a save the control was not applied to.
 */
export type TemplateQualityGateStatus =
  | TemplateQualityGateDecision
  | 'shown'
  | 'bypassed';

export type TemplateQualityGateEventProps = {
  gate_status: TemplateQualityGateStatus;
  surface: TemplateQualityGateSurface;
  /**
   * The authoritative Kinde region — deliberately not `region`, which other
   * events derive from a spoofable source that must not gate a medical-device
   * decision. Reusing that name would mix the two in one column.
   */
  user_region?: Region;
  region_status: TemplateQualityGateRegionStatus;
  is_kill_switch_on: boolean;
  /**
   * The template in hand at gate time, not the one the save goes on to create:
   * an edit or a create-by-copy carries the source template, and a create from
   * scratch carries nothing at all.
   */
  template_id?: string;
};

/**
 * The gate's domain vocabulary in the wire property names the warehouse keys
 * on.
 *
 * A pure mapping, and on THIS side of the seam on purpose: the property names
 * are the event's contract, so an app host that re-derived them would be
 * spelling a schema it does not own. What the app adds is the `event_name` and
 * the destination routing, and nothing else (ruling 24).
 */
export const buildTemplateQualityGateEventProps = (
  gateStatus: TemplateQualityGateStatus,
  context: TemplateQualityGateSaveContext
): TemplateQualityGateEventProps => ({
  gate_status: gateStatus,
  surface: context.surface,
  user_region: context.userRegion,
  region_status: context.regionStatus,
  is_kill_switch_on: context.isKillSwitchOn,
  template_id: context.templateId,
});

/**
 * The emit seam for one gate report.
 *
 * It takes the domain's status-and-context pair rather than the built payload,
 * because the two callers that hold one — the save guard and the dialog — hold
 * exactly that and nothing else. The app-side binding maps it through
 * {@link buildTemplateQualityGateEventProps}, so the payload is still the
 * domain's; what crosses is which event and where it is sent.
 */
export type TrackTemplateQualityGate = (
  gateStatus: TemplateQualityGateStatus,
  context: TemplateQualityGateSaveContext
) => void;
